import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { EmailService } from '../../services/email/email.service';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-newsletter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    TranslateModule
  ],
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.scss']
})
export class NewsletterComponent implements OnInit {
  form!: FormGroup;
  formSubmitted = false;
  subscriptionEmailErrorKey: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _sendEmail: EmailService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      acceptedDisclaimer: [{ value: false, disabled: true }, Validators.requiredTrue]
    });

    this.email?.statusChanges.subscribe(status => {
      const emailValid = status === 'VALID';
      if (emailValid) {
        this.acceptedDisclaimer?.enable({ emitEvent: false });
      } else {
        this.acceptedDisclaimer?.disable({ emitEvent: false });
        this.acceptedDisclaimer?.setValue(false, { emitEvent: false });
      }
    });

    this.translate.onLangChange.subscribe(() => {
      this.updateErrorMessages();
    });
  }

  get email() {
    return this.form.get('email');
  }

  get acceptedDisclaimer() {
    return this.form.get('acceptedDisclaimer');
  }

  async onSubscribe() {
    this.subscriptionEmailErrorKey = null;
    this.formSubmitted = false;

    if (this.form.invalid) {
      this.form.markAllAsTouched();

      if (this.email?.invalid) {
        this.subscriptionEmailErrorKey = 'newsletter.global_error_invalid_email';
      } else if (this.acceptedDisclaimer?.invalid) {
        this.subscriptionEmailErrorKey = 'newsletter.terms_required_error';
      } else {
        this.subscriptionEmailErrorKey = 'newsletter.global_error_form_invalid';
      }

      return;
    }

    try {
      await firstValueFrom(this._sendEmail.subscribeToNewsletter({ email: this.email?.value }));
      this.resetForm();
      this.formSubmitted = true;

      setTimeout(() => {
        this.formSubmitted = false;
      }, 4000);
    } catch (error) {
      this.handleSubscriptionError(error);
    }
  }

  resetForm(): void {
    this.form.reset({ email: '', acceptedDisclaimer: false });
    this.acceptedDisclaimer?.disable({ emitEvent: false });
    this.subscriptionEmailErrorKey = null;
  }

  handleSubscriptionError(error: any): void {
    const alreadySubscribedKey = 'newsletter.global_error_already_subscribed';
    this.subscriptionEmailErrorKey = error.message === this.translate.instant(alreadySubscribedKey)
      ? alreadySubscribedKey
      : 'newsletter.global_error_generic';
  }

  private updateErrorMessages(): void {
    this.email?.updateValueAndValidity({ emitEvent: false });
    this.acceptedDisclaimer?.updateValueAndValidity({ emitEvent: false });
  }
}
